import React from 'react';
import { Link } from 'react-router-dom';
import WebPImage from './WebPImage';
import { logo, line2 } from '../assets';
import useSystemConfig from '../hooks/useSystemConfig';

const Footer: React.FC = () => {
  const { config, loading } = useSystemConfig();

  if (loading) {
    return (
      <footer className="bg-[#1a1e23] text-[#f4f2e5] py-[56px] sm:py-12 lg:py-12 pb-12 sm:pb-12 lg:pb-12 w-full">
        <div className="w-full px-[36px] sm:px-8 lg:px-[60px]">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-700 rounded mb-4"></div>
            <div className="h-4 bg-gray-700 rounded mb-2"></div>
            <div className="h-4 bg-gray-700 rounded mb-2"></div>
            <div className="h-4 bg-gray-700 rounded"></div>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="bg-[#1a1e23] text-[#f4f2e5] py-[56px] sm:py-12 lg:py-12 pb-12 sm:pb-12 lg:pb-12 w-full">
      <div className="w-full px-[36px] sm:px-8 lg:px-[60px]">
        {/* Logo */}
        <div className="mb-8 sm:mb-12 lg:mb-16">
          <div 
            className="h-[65px] w-[208px] sm:h-[78px] sm:w-[247px] lg:h-[91px] lg:w-[293px] bg-contain bg-center bg-no-repeat" 
            style={{ backgroundImage: `url('${logo}')` }} 
          />
        </div>
        
        {/* Main Title */}
        <div className="mb-8 sm:mb-12 lg:mb-16">
          <h2 className="text-[24px] sm:text-[32px] lg:text-[40px] font-normal leading-[1.6] sm:leading-[1.6] lg:leading-[1.6] max-w-[826px] text-left">
            {config?.site_tagline || 'Bringing Coimbatore to Chennai since 2012'}
          </h2>
        </div>
        
        {/* Footer Columns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-6 mb-12 sm:mb-14 lg:mb-16">
          {/* Get in touch */}
          <div>
            <div className="border-b border-[rgba(244,242,228,0.2)] pb-2 mb-6 sm:mb-8 lg:mb-[51.38px]">
              <h3 className="text-[18px] sm:text-[19px] lg:text-[21px] font-bold leading-[1.6] sm:leading-[1.6] lg:leading-[1.6] text-left">Get in touch</h3>
            </div>
            <div className="space-y-4 sm:space-y-5 lg:space-y-[30px]">
              <p className="text-[15px] sm:text-[16px] lg:text-[17px] font-light leading-[1.6] sm:leading-[1.6] lg:leading-[1.6] text-left">{config?.site_name || 'Siruvani Foundation'}</p>
              <p className="text-[15px] sm:text-[16px] lg:text-[17px] font-light leading-[1.6] sm:leading-[1.6] lg:leading-[1.6] text-left">{config?.site_address || 'TTK Road, Alwarpet, Chennai, India'}</p>
              <p className="text-[15px] sm:text-[16px] lg:text-[17px] font-light leading-[1.6] sm:leading-[1.6] lg:leading-[1.6] text-left">{config?.site_email || 'info@siruvani.com'}</p>
              <p className="text-[15px] sm:text-[16px] lg:text-[17px] font-light leading-[1.6] sm:leading-[1.6] lg:leading-[1.6] text-left">{config?.site_phone || '+91 98766 54321'}</p>
            </div>
          </div>
          
          {/* Quick links */}
          <div>
            <div className="border-b border-[rgba(244,242,228,0.2)] pb-2 mb-6 sm:mb-8 lg:mb-[51.38px]">
              <h3 className="text-[18px] sm:text-[19px] lg:text-[21px] font-bold leading-[1.6] sm:leading-[1.6] lg:leading-[1.6] text-left">Quick links</h3>
            </div>
            <div className="space-y-4 sm:space-y-5 lg:space-y-[35.68px]">
              <Link to="/about" className="block text-[15px] sm:text-[16px] lg:text-[17px] font-light leading-[1.6] sm:leading-[1.6] lg:leading-[1.6] text-left hover:text-[#c6d042] transition-colors">
                About Us
              </Link>
              <Link to="/events" className="block text-[15px] sm:text-[16px] lg:text-[17px] font-light leading-[1.6] sm:leading-[1.6] lg:leading-[1.6] text-left hover:text-[#c6d042] transition-colors">
                Events
              </Link>
              <Link to="/calendar" className="block text-[15px] sm:text-[16px] lg:text-[17px] font-light leading-[1.6] sm:leading-[1.6] lg:leading-[1.6] text-left hover:text-[#c6d042] transition-colors">
                Event Calendar
              </Link>
              <Link to="/gallery" className="block text-[15px] sm:text-[16px] lg:text-[17px] font-light leading-[1.6] sm:leading-[1.6] lg:leading-[1.6] text-left hover:text-[#c6d042] transition-colors">
                Gallery
              </Link>
            </div>
          </div>
          
          {/* Legal */}
          <div>
            <div className="border-b border-[rgba(244,242,228,0.2)] pb-2 mb-6 sm:mb-8 lg:mb-[51.38px]">
              <h3 className="text-[18px] sm:text-[19px] lg:text-[21px] font-bold leading-[1.6] sm:leading-[1.6] lg:leading-[1.6] text-left">Legal</h3>
            </div>
            <div className="space-y-4 sm:space-y-5 lg:space-y-[35.68px]">
              <Link to="/privacy" className="block text-[15px] sm:text-[16px] lg:text-[17px] font-light leading-[1.6] sm:leading-[1.6] lg:leading-[1.6] text-left hover:text-[#c6d042] transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="block text-[15px] sm:text-[16px] lg:text-[17px] font-light leading-[1.6] sm:leading-[1.6] lg:leading-[1.6] text-left hover:text-[#c6d042] transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
          
          {/* Community */}
          <div>
            <div className="border-b border-[rgba(244,242,228,0.2)] pb-2 mb-6 sm:mb-8 lg:mb-[51.38px]">
              <h3 className="text-[18px] sm:text-[19px] lg:text-[21px] font-bold leading-[1.6] sm:leading-[1.6] lg:leading-[1.6] text-left">Community</h3>
            </div>
            <div className="space-y-4 sm:space-y-5 lg:space-y-[35.68px]">
              <Link to="/membership" className="block text-[15px] sm:text-[16px] lg:text-[17px] font-light leading-[1.6] sm:leading-[1.6] lg:leading-[1.6] text-left hover:text-[#c6d042] transition-colors">
                Membership
              </Link>
              <Link to="/join" className="block text-[15px] sm:text-[16px] lg:text-[17px] font-light leading-[1.6] sm:leading-[1.6] lg:leading-[1.6] text-left hover:text-[#c6d042] transition-colors">
                Join Now
              </Link>
              <div className="flex items-center gap-3 sm:gap-4">
                {config?.facebook_url && (
                  <a 
                    href={config.facebook_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-8 h-8 sm:w-8 sm:h-8 flex items-center justify-center hover:scale-110 transition-transform duration-300"
                  >
                    <img 
                      src="/facebook-icon-correct.svg" 
                      alt="Facebook" 
                      className="w-full h-full object-contain" 
                    />
                  </a>
                )}
                {config?.instagram_url && (
                  <a 
                    href={config.instagram_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-8 h-8 sm:w-8 sm:h-8 flex items-center justify-center hover:scale-110 transition-transform duration-300"
                  >
                    <img 
                      src="/instagram-icon.svg" 
                      alt="Instagram" 
                      className="w-full h-full object-contain" 
                    />
                  </a>
                )}
                {config?.linkedin_url && (
                  <a 
                    href={config.linkedin_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-8 h-8 sm:w-8 sm:h-8 flex items-center justify-center hover:scale-110 transition-transform duration-300"
                  >
                    <img 
                      src="/linkedin-icon.svg" 
                      alt="LinkedIn" 
                      className="w-full h-full object-contain" 
                    />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Divider Line */}
        <div className="w-full h-px mb-8 sm:mb-12 lg:mb-16">
          <img src={line2} alt="Decorative line" className="w-full h-full" />
        </div>
        
        {/* Copyright */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 sm:gap-4">
          <div>
            <p className="text-[14px] sm:text-[15px] lg:text-[16px] font-thin leading-[1.6] sm:leading-[1.6] lg:leading-[1.6] text-left">
              © Copyright {new Date().getFullYear()} {config?.site_name || 'Siruvani Foundation'}  |  All rights reserved
            </p>
          </div>
          
          <a 
            href="https://uitouxsolutions.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-[15px] sm:text-[16px] lg:text-[17px] font-thin leading-[1.6] sm:leading-[1.6] lg:leading-[1.6] text-right transition-all duration-700 ease-in-out relative group"
          >
            <span className="relative z-10 group-hover:scale-105 transition-transform duration-700 flex items-center justify-start sm:justify-end gap-2">
              <span className="group-hover:text-[#65DBA6] transition-colors duration-700">Crafted</span>
              <span className="group-hover:text-[#65DBA6] transition-colors duration-700">with</span>
              <span style={{color: '#ff4f00'}} className="group-hover:text-[#65DBA6] group-hover:scale-125 transition-all duration-700 inline-block">♥</span>
              <span className="group-hover:text-[#65DBA6] transition-colors duration-700">by</span>
              <span className="group-hover:text-[#65DBA6] transition-colors duration-700 flex items-center gap-1">
                <WebPImage 
                  src="/uitoux.png" 
                  webpSrc="/uitoux.png"
                  alt="UITOUX Logo" 
                  className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 object-contain"
                />
                <span className="relative after:content-[''] after:absolute after:bottom-[-2px] after:left-0 after:w-0 after:h-[2px] after:bg-[#ff4f00] after:transition-all after:duration-700 after:ease-out group-hover:after:w-full">UITOUX</span>
              </span>
            </span>
            <div className="absolute -inset-1 bg-gradient-to-r from-[#feca57] via-[#ff3838] to-[#ff9ff3] opacity-0 group-hover:opacity-20 transition-opacity duration-700 rounded-md blur-md -z-10"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-[#feca57] via-[#ff3838] to-[#ff9ff3] opacity-0 group-hover:opacity-5 transition-opacity duration-700 rounded-md -z-10"></div>
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
